<template>
  <CModal
    ref="modalRef"
    class="modal-v3"
    :class="{
      'd-flex justify-content-center align-items-center': placeModalOnCenter
    }"
    backdrop="static"
    :close-on-backdrop="false"
    v-bind="{ visible, size, scrollable }"
    @close="closeModal"
  >
    <!-- Header -->
    <div
      v-if="!hideHeader"
      class="modal-v3-header create-popup-header d-flex align-items-center justify-content-between"
    >
      <span v-if="help">
        <HelpCenterButton :content-key="help" :is-old="isHelpOld"
      /></span>
      <div class="modal-v3-header-text flex-1 text-center">
        <h2
          v-if="resolvedTitle"
          :id="titleHeadingId"
          class="modal-v3-header-heading mb-0"
          tabindex="-1"
        >
          {{ resolvedTitle }}
        </h2>
        <div
          v-if="!title"
          class="modal-v3-header-slot"
          role="heading"
          aria-level="2"
        >
          <slot name="header"> </slot>
        </div>
        <slot v-else name="header"> </slot>
      </div>
      <div v-if="!hideCloseButton" class="text-end close-btn">
        <button
          type="button"
          class="close-modal-action btn btn-link p-0 border-0"
          aria-label="Close"
          :tabindex="visible ? 0 : -1"
          :aria-hidden="visible ? undefined : 'true'"
          @click="closeModal"
        >
          <i class="ri-close-line cursor-pointer" aria-hidden="true" />
        </button>
      </div>
    </div>
    <!-- Body -->
    <div class="modal-v3-body">
      <component
        @cancel="closeModal"
        v-if="modal && modal.component"
        :is="modal.component"
        v-bind="modal.options.props"
        v-on="modal.options.props.events || {}"
      ></component>
      <slot></slot>
    </div>
    <!-- Footer -->
    <div class="modal-v3-footer">
      <slot name="footer"> </slot>
    </div>
  </CModal>
</template>

<script>
import { computed, inject, nextTick, onUnmounted, ref, watch } from "vue"
import { onBeforeRouteUpdate } from "vue-router"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import { shouldDeferModalEscape } from "@/helpers/isPickerOverlayOpen"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",")

export default {
  components: { HelpCenterButton },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "sm"
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    help: {
      type: String,
      default: ""
    },
    isHelpOld: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    hideCloseButton: {
      type: Boolean,
      default: false
    },
    addOnModalClose: {
      type: Boolean,
      default: false
    },
    onModalClose: {
      type: Function,
      default: () => ({})
    },
    placeModalOnCenter: {
      type: Boolean,
      default: false
    }
  },
  emits: ["close"],
  setup(props, { emit }) {
    const modal = inject("modal")
    const modalRef = ref(null)
    const ownedModalElement = ref(null)
    const titleHeadingId = ref(
      `modal-v3-title-${Math.random().toString(36).slice(2, 11)}`
    )
    const isModalVisible = computed(() => Boolean(props.visible))
    const lastFocusedElement = ref(null)

    const resolvedTitle = computed(
      () => props.title || modal?.options?.title || ""
    )

    const canRestoreFocus = (target) => {
      if (!target || typeof target.focus !== "function") return false
      if (target.isConnected === false) return false
      if ("ownerDocument" in target && target.ownerDocument) {
        return target.ownerDocument.contains(target)
      }
      return true
    }

    const restoreFocus = (target) => {
      if (!canRestoreFocus(target)) return
      try {
        target.focus()
      } catch {
        // no-op for transient DOM states during modal transitions
      }
    }

    const isVisibleElement = (element) => {
      if (!element) return false
      return element.offsetWidth > 0 || element.offsetHeight > 0
    }

    const hasVisibleModalState = (element) => {
      if (!element) return false
      const classList = element.classList
      if (classList?.contains("show")) return true
      if (element.getAttribute("aria-modal") === "true") return true
      const style = element.getAttribute("style") || ""
      return style.includes("display: block")
    }

    const getVisibleModalCandidates = () => {
      return Array.from(document.querySelectorAll(".modal-v3, .modal")).filter(
        (element) => hasVisibleModalState(element) && isVisibleElement(element)
      )
    }

    const getCurrentModalElement = () => {
      if (
        ownedModalElement.value &&
        ownedModalElement.value.isConnected &&
        isVisibleElement(ownedModalElement.value)
      ) {
        return ownedModalElement.value
      }

      const refElement = modalRef.value?.$el || modalRef.value
      if (refElement) {
        if (
          refElement.classList?.contains("modal-v3") ||
          refElement.classList?.contains("modal")
        ) {
          return refElement
        }
        const nestedDialog = refElement.querySelector?.(".modal-dialog")
        const closestModal = nestedDialog?.closest?.(".modal-v3, .modal")
        if (closestModal) return closestModal
        const nestedModal = refElement.querySelector?.(".modal-v3, .modal")
        if (nestedModal) return nestedModal
      }

      const titleHeading = document.getElementById(titleHeadingId.value)
      const titleModal = titleHeading?.closest?.(".modal-v3, .modal")
      if (titleModal) return titleModal

      const visibleModals = getVisibleModalCandidates()
      if (visibleModals.length === 1) return visibleModals[0]

      // Fallback for teleported/transitioning CoreUI DOM where ownership
      // cannot be resolved synchronously: keep behavior tied to the top-most dialog.
      if (visibleModals.length > 1) {
        return visibleModals[visibleModals.length - 1]
      }

      return null
    }

    const getTopMostModalElement = () => {
      const visibleModals = getVisibleModalCandidates()
      if (visibleModals.length) {
        return visibleModals[visibleModals.length - 1]
      }
      return null
    }

    const isCurrentModalTopMost = () => {
      const currentModal = getCurrentModalElement()
      const topMostModal = getTopMostModalElement()
      if (!currentModal && !topMostModal) return false
      if (currentModal && !topMostModal) return true
      if (!currentModal && topMostModal) return false
      return currentModal === topMostModal
    }

    const applyDialogAccessibility = () => {
      if (!isModalVisible.value) return
      nextTick(() => {
        window.setTimeout(() => {
          const modalElement = getCurrentModalElement()
          const dlg = modalElement?.querySelector?.(".modal-dialog")
          if (!dlg) return
          dlg.setAttribute("aria-modal", "true")
          if (resolvedTitle.value && !props.hideHeader) {
            dlg.setAttribute("aria-labelledby", titleHeadingId.value)
          } else {
            dlg.removeAttribute("aria-labelledby")
          }
        }, 0)
      })
    }

    watch(
      [isModalVisible, resolvedTitle, () => props.hideHeader],
      applyDialogAccessibility,
      { immediate: true }
    )

    const getVisibleFocusableElements = (modalElement) => {
      if (!modalElement) return []
      return Array.from(
        modalElement.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((el) => {
        return (
          !el.hasAttribute("disabled") &&
          el.getAttribute("aria-hidden") !== "true" &&
          (el.offsetWidth > 0 || el.offsetHeight > 0)
        )
      })
    }

    const isEscapePress = (event) => {
      return (
        event?.key === "Escape" ||
        event?.key === "Esc" ||
        event?.code === "Escape" ||
        event?.keyCode === 27
      )
    }
    const focusFirstModalElement = () => {
      const modalElement = getCurrentModalElement()
      if (!modalElement) return

      const titleHeading = modalElement.querySelector(
        ".modal-v3-header-heading"
      )
      if (
        titleHeading &&
        resolvedTitle.value &&
        !props.hideHeader &&
        typeof titleHeading.focus === "function"
      ) {
        titleHeading.focus()
        return
      }

      const focusableElements = getVisibleFocusableElements(modalElement)
      if (focusableElements.length) {
        focusableElements[0].focus()
      } else {
        modalElement.setAttribute("tabindex", "-1")
        modalElement.focus()
      }
    }

    const isVuepicDatepickerOverlay = (element) => {
      return Boolean(element?.closest?.(".dp__outer_menu_wrap, .dp__menu"))
    }

    /**
     * vue-select may append the listbox to `document.body`; our focus trap must not
     * pull focus back into the modal while the user is interacting with the menu.
     */
    const isFocusInsidePortaledVueSelectMenu = (target, modalElement) => {
      if (!target || typeof target.closest !== "function" || !modalElement) {
        return false
      }
      const menu = target.closest(".vs__dropdown-menu")
      if (!menu) return false
      return !modalElement.contains(menu)
    }

    const onDocumentKeydown = (event) => {
      if (!isModalVisible.value || !isCurrentModalTopMost()) return

      if (isEscapePress(event)) {
        // Defer modal close only: do not stop the event — capture-phase
        // stopPropagation/preventDefault would block open pickers (date/time
        // overlays) from receiving Escape to dismiss themselves first.
        if (shouldDeferModalEscape(event)) {
          return
        }
        event.preventDefault()
        event.stopPropagation()
        closeModal()
        return
      }

      if (event.key !== "Tab") return

      const modalElement = getCurrentModalElement()
      if (!modalElement) return

      const focusableElements = getVisibleFocusableElements(modalElement)
      const activeElement = document.activeElement

      // Keep focus inside the dialog when Tab moves to content behind the modal (e.g. portaled widgets).
      if (
        focusableElements.length &&
        activeElement &&
        !modalElement.contains(activeElement)
      ) {
        // Vue Datepicker may teleport the menu to body; focus is still valid for the open picker.
        if (isVuepicDatepickerOverlay(activeElement)) {
          return
        }
        if (isFocusInsidePortaledVueSelectMenu(activeElement, modalElement)) {
          return
        }
        event.preventDefault()
        const target =
          focusableElements[event.shiftKey ? focusableElements.length - 1 : 0]
        target?.focus?.()
        return
      }

      if (!focusableElements.length) {
        event.preventDefault()
        modalElement.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    const onDocumentFocusIn = (event) => {
      if (!isModalVisible.value || !isCurrentModalTopMost()) return

      const modalElement = getCurrentModalElement()
      if (!modalElement) return

      if (event.target && !modalElement.contains(event.target)) {
        // Teleported date/time picker menus stay outside .modal in the DOM; do not yank focus.
        if (isVuepicDatepickerOverlay(event.target)) {
          return
        }
        if (isFocusInsidePortaledVueSelectMenu(event.target, modalElement)) {
          return
        }
        const focusableElements = getVisibleFocusableElements(modalElement)
        const target = focusableElements[0] || modalElement
        target?.focus?.()
      }
    }

    const closeModal = () => {
      if (props.addOnModalClose) {
        props.onModalClose()
      }
      emit("close")
    }

    watch(
      isModalVisible,
      (visible) => {
        if (visible) {
          lastFocusedElement.value = document.activeElement
          nextTick(() => {
            window.setTimeout(() => {
              ownedModalElement.value = getCurrentModalElement()
            }, 0)
            focusFirstModalElement()
          })
        } else {
          ownedModalElement.value = null
          restoreFocus(lastFocusedElement.value)
          lastFocusedElement.value = null
        }
      },
      { immediate: true }
    )

    document.addEventListener("keydown", onDocumentKeydown, true)
    document.addEventListener("focusin", onDocumentFocusIn, true)

    onUnmounted(() => {
      document.removeEventListener("keydown", onDocumentKeydown, true)
      document.removeEventListener("focusin", onDocumentFocusIn, true)
    })

    onBeforeRouteUpdate(() => {
      closeModal()
    })

    return {
      closeModal,
      modal,
      titleHeadingId,
      resolvedTitle,
      modalRef
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/assets/scss/_variables.scss";

.close-modal-action:focus-visible {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
  border-radius: 2px;
}

.modal-v3-body,
.modal-v3-footer,
.modal-v3-header {
  :deep(.v3-input-field input),
  :deep(.v3-input-field textarea) {
    border-color: $gray-border;
  }
  :deep(.v3-input-field textarea) {
    resize: none !important;
  }

  :deep(.v3-base-btn:focus-visible),
  :deep(.v3-loading-button .v3-base-btn:focus-visible),
  :deep(.v3-icon-button:focus-visible),
  :deep(.v3-pill-button:focus-visible),
  :deep(button.visitor-tab:focus-visible),
  :deep(.visitor-info-icon-btn:focus-visible),
  :deep(button.btn:not(.btn-link):focus-visible),
  :deep(input:focus-visible),
  :deep(textarea:focus-visible),
  :deep(select:focus-visible),
  :deep(.vs__dropdown-toggle:focus-within) {
    outline: 2px solid $outline-color;
    outline-offset: $outline-offset;
  }
}

.modal-v3-body :deep(.v3-input-field input::placeholder),
.modal-v3-body :deep(.v3-input-field textarea::placeholder) {
  color: #4a4a4a;
  opacity: 1;
}

// Muted body copy in modals (Bootstrap text-black-50 is too light on white)
.modal-v3-body :deep(.text-black-50) {
  color: #4a4a4a !important;
}
</style>
